import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle } from 'lucide-react';
function LegalModal({
  open,
  onClose,
  title,
  children
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-108">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]" data-id="element-109" />
          <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 40
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed inset-4 md:inset-x-auto md:inset-y-8 md:max-w-2xl md:mx-auto bg-white rounded-2xl z-[101] overflow-hidden flex flex-col shadow-2xl" data-id="element-110">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0" data-id="element-111">
              <h2 className="font-serif text-xl font-bold text-[#0f172a]" data-id="element-112">
                {title}
              </h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#f1f5f9] flex items-center justify-center hover:bg-[#e2e8f0] transition-colors cursor-pointer" data-id="element-113">
                <X className="w-4 h-4 text-[#64748b]" data-id="element-114" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6 text-[#475569] text-sm leading-relaxed space-y-4" data-id="element-115">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  return <>
      <footer className="w-full bg-[#0a0f1a] px-4 pt-12 pb-8" data-id="element-116">
        <div className="max-w-3xl mx-auto" data-id="element-117">
          {/* Disclaimer */}
          <div className="bg-[#1e293b]/60 border border-[#334155] rounded-xl p-6 mb-10" data-id="element-118">
            <div className="flex items-start gap-3 mb-3" data-id="element-119">
              <AlertTriangle className="w-5 h-5 text-[#fbbf24] shrink-0 mt-0.5" data-id="element-120" />
              <p className="text-[#fbbf24] text-sm font-semibold uppercase tracking-wide" data-id="element-121">
                Aviso Importante
              </p>
            </div>
            <p className="text-[#94a3b8] text-xs leading-relaxed pl-8" data-id="element-122">
              Este producto es una{' '}
              <strong className="text-[#cbd5e1]" data-id="element-123">guía educativa</strong>, no
              consejo médico. No reemplaza la consulta con un pediatra o
              profesional de salud. Si tu bebé presenta fiebre, dificultad para
              respirar, llanto inconsolable u otra señal de alarma,{' '}
              <strong className="text-[#cbd5e1]" data-id="element-124">
                consulta a un médico de inmediato.
              </strong>{' '}
              Los resultados varían según cada bebé.
            </p>
          </div>

          {/* Footer links */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-t border-[#1e293b] pt-8" data-id="element-125">
            <div className="text-center md:text-left" data-id="element-126">
              <p className="text-[#cbd5e1] text-sm font-medium" data-id="element-127">
                Better by Steps
              </p>
              <p className="text-[#64748b] text-xs mt-1" data-id="element-128">
                © {new Date().getFullYear()} Better by Steps. Todos los
                derechos reservados.
              </p>
            </div>
            <div className="flex items-center gap-6" data-id="element-129">
              <button onClick={() => setTermsOpen(true)} className="text-[#94a3b8] hover:text-[#cbd5e1] text-xs underline underline-offset-2 transition-colors cursor-pointer" data-id="element-130">
                Términos y Condiciones
              </button>
              <button onClick={() => setPrivacyOpen(true)} className="text-[#94a3b8] hover:text-[#cbd5e1] text-xs underline underline-offset-2 transition-colors cursor-pointer" data-id="element-131">
                Política de Privacidad
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      <LegalModal open={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-132">
        <p className="font-semibold text-[#0f172a]" data-id="element-133">
          Última actualización: Febrero 2026
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-134">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-135">
          Al realizar la compra de "2AM Newborn Sleep: Panic-to-Plan Scripts"
          (en adelante, "el Producto"), aceptas estos Términos y Condiciones en
          su totalidad. Si no estás de acuerdo, no realices la compra.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-136">
          2. Descripción del Producto
        </h3>
        <p data-id="element-137">
          El Producto es un recurso digital descargable en formato PDF que
          incluye guiones, un árbol de decisión, tarjetas imprimibles y un
          tracker de 14 noches. Es una guía educativa e informativa, no un
          servicio médico ni de consultoría profesional.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-138">
          3. Entrega
        </h3>
        <p data-id="element-139">
          El Producto se entrega de forma digital e inmediata tras la
          confirmación del pago. Recibirás un correo electrónico con el enlace
          de descarga. Es tu responsabilidad proporcionar un correo electrónico
          válido.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-140">
          4. Precio y Pago
        </h3>
        <p data-id="element-141">
          El precio del Producto es el indicado en la página de venta al momento
          de la compra. El pago se procesa de forma segura a través de Shopify.
          Es un pago único, sin suscripciones ni cargos recurrentes.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-142">
          5. Política de Reembolso
        </h3>
        <p data-id="element-143">
          Ofrecemos una garantía de satisfacción. Si el Producto no cumple tus
          expectativas, puedes solicitar un reembolso completo contactándonos
          por correo electrónico. No se requiere justificación.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-144">
          6. Propiedad Intelectual
        </h3>
        <p data-id="element-145">
          Todo el contenido del Producto (textos, diseños, estructura, guiones)
          es propiedad de Better by Steps. La compra otorga una licencia
          personal e intransferible. Queda prohibida la reproducción,
          distribución, reventa o modificación del Producto sin autorización
          expresa.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-146">
          7. Uso del Producto
        </h3>
        <p data-id="element-147">
          El Producto está diseñado para uso personal y familiar. No debe
          utilizarse como sustituto de atención médica profesional. Better by
          Steps no se responsabiliza por el uso inadecuado del contenido.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-148">
          8. Limitación de Responsabilidad
        </h3>
        <p data-id="element-149">
          Better by Steps no garantiza resultados específicos. Los resultados
          varían según cada bebé y situación familiar. En ningún caso seremos
          responsables por daños directos, indirectos o consecuentes derivados
          del uso del Producto.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-150">
          9. Contacto
        </h3>
        <p data-id="element-151">
          Para consultas sobre estos términos, escríbenos a:{' '}
          <span className="text-[#5b8a7a] font-medium" data-id="element-152">
            hello@betterbysteps.com
          </span>
        </p>
      </LegalModal>

      {/* Privacy Modal */}
      <LegalModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-153">
        <p className="font-semibold text-[#0f172a]" data-id="element-154">
          Última actualización: Febrero 2026
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-155">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-156">
          Al realizar tu compra, recopilamos únicamente la información necesaria
          para procesar tu pedido:
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-157">
          <li data-id="element-158">Nombre</li>
          <li data-id="element-159">Dirección de correo electrónico</li>
          <li data-id="element-160">
            Información de pago (procesada de forma segura por Shopify; no
            almacenamos datos de tarjeta)
          </li>
        </ul>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-161">
          2. Uso de la Información
        </h3>
        <p data-id="element-162">Utilizamos tu información exclusivamente para:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-163">
          <li data-id="element-164">Procesar y entregar tu compra</li>
          <li data-id="element-165">Enviarte el enlace de descarga del Producto</li>
          <li data-id="element-166">Comunicarnos contigo si hay algún problema con tu pedido</li>
          <li data-id="element-167">Enviarte actualizaciones del Producto (si aplica)</li>
        </ul>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-168">
          3. Protección de Datos
        </h3>
        <p data-id="element-169">
          Tu información personal está protegida mediante los protocolos de
          seguridad de Shopify (cifrado SSL). No vendemos, alquilamos ni
          compartimos tu información personal con terceros, excepto lo necesario
          para procesar tu pago.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-170">
          4. Cookies
        </h3>
        <p data-id="element-171">
          Nuestra página puede utilizar cookies esenciales para el
          funcionamiento del sitio y cookies analíticas para entender cómo los
          visitantes interactúan con la página. Puedes desactivar las cookies en
          la configuración de tu navegador.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-172">
          5. Tus Derechos
        </h3>
        <p data-id="element-173">Tienes derecho a:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-174">
          <li data-id="element-175">Acceder a la información personal que tenemos sobre ti</li>
          <li data-id="element-176">Solicitar la corrección de datos inexactos</li>
          <li data-id="element-177">Solicitar la eliminación de tus datos personales</li>
          <li data-id="element-178">Retirar tu consentimiento para comunicaciones</li>
        </ul>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-179">
          6. Retención de Datos
        </h3>
        <p data-id="element-180">
          Conservamos tu información personal mientras sea necesario para
          cumplir con los fines descritos en esta política, o según lo requiera
          la ley aplicable.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-181">
          7. Cambios en esta Política
        </h3>
        <p data-id="element-182">
          Nos reservamos el derecho de actualizar esta Política de Privacidad.
          Cualquier cambio será publicado en esta página con la fecha de
          actualización correspondiente.
        </p>

        <h3 className="font-serif font-bold text-[#0f172a] text-base mt-4" data-id="element-183">
          8. Contacto
        </h3>
        <p data-id="element-184">
          Para ejercer tus derechos o resolver dudas sobre privacidad,
          escríbenos a:{' '}
          <span className="text-[#5b8a7a] font-medium" data-id="element-185">
            hello@betterbysteps.com
          </span>
        </p>
      </LegalModal>
    </>;
}